import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useProductionVessels, useRenameProductionRow, useToggleTask, useVesselDetail, type VesselDetail } from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { api } from '../lib/api.js';
import { useIsPhone, useMediaQuery } from '../lib/media.js';
import { useDateFormatter } from '../lib/format.js';
import { fold, hasText } from '../lib/search.js';
import { openPrintWindow, renderPrint } from '../features/exportSchedule.js';
import { EmptyState, ErrorState, InlineError, LoadingState, PageHeader, ProgressBar, SyncLabel } from '../components/ui.js';
import { IconChevronRight, IconPrinter } from '../components/icons.js';

const esc = (s: string) => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

// Reserved slot / placeholder rows ("GT7_SLOT 2", "40_TBD") never reach this page: the server leaves out
// any that hasn't been given a SAP. A real boat can still be waiting for its SAP, so it's shown (and can be
// hidden with the toggle), and boats are told apart by their own row on the sheet, never by SAP.
const hasSap = (sap: string): boolean => /\d/.test(sap);

// at and below this width the list and the checklist stack, so the page works master-detail: picking a
// vessel swaps the list for its checklist (with a back bar), instead of opening it below the fold.
// Same string as the .asm-progress rules in global.css (Assembly Progress).
const STACKED = '(max-width:760px)';
const scrollContent = () => document.querySelector('main.content');

/** a name that turns into a text box on a right-click (a long-press on Android): Enter or clicking away
 *  saves, Escape cancels. `startEditing` opens it from outside (a right-click on the vessel in the list). */
function Renamable({ value, label, canRename, onRename, startEditing = false, onEditEnd, className, style }: {
  value: string;
  label: string;
  canRename: boolean;
  onRename: (next: string) => void;
  startEditing?: boolean;
  onEditEnd?: () => void;
  className?: string;
  style?: CSSProperties;
}) {
  const [editing, setEditing] = useState(false);
  const cancelled = useRef(false);
  useEffect(() => { if (startEditing && canRename) setEditing(true); }, [startEditing, canRename]);
  if (editing) {
    return (
      <input
        className="field asm-rename"
        autoFocus
        defaultValue={value}
        aria-label={`Rename ${label}`}
        onFocus={(e) => e.currentTarget.select()}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === 'Escape') { e.preventDefault(); cancelled.current = true; e.currentTarget.blur(); }
        }}
        onBlur={(e) => {
          const next = e.currentTarget.value.trim();
          setEditing(false);
          onEditEnd?.();
          if (!cancelled.current && next && next !== value) onRename(next);
          cancelled.current = false;
        }}
      />
    );
  }
  return (
    <span
      className={className}
      style={style}
      title={canRename ? 'Right-click to rename' : undefined}
      onContextMenu={canRename ? (e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); } : undefined}
    >{value}</span>
  );
}

export function AssemblyProgress() {
  const { can } = useAuth();
  const { activeSeason } = useSeason();
  const canWrite = can('production:write');
  const list = useProductionVessels();
  const df = useDateFormatter();
  const [q, setQ] = useState('');
  const [model, setModel] = useState('all');
  const [hideNoSap, setHideNoSap] = useState(false); // boats still waiting for a SAP: shown unless hidden
  const [boat, setBoat] = useState<number | null>(null); // the open vessel: its own row on the sheet
  const [selected, setSelected] = useState<Set<number>>(new Set()); // vessels chosen for print (by row)
  const [printing, setPrinting] = useState(false);
  const [printErr, setPrintErr] = useState<Error | null>(null);

  const stacked = useMediaQuery(STACKED);
  const isPhone = useIsPhone();
  const current = useMemo(() => (list.data?.vessels ?? []).find((v) => v.rowId === boat) ?? null, [list.data, boat]);
  const detail = useVesselDetail(current ? { sap: current.sap, boat: current.rowId } : null);
  const toggle = useToggleTask();
  const rename = useRenameProductionRow();
  const [renameBoat, setRenameBoat] = useState<number | null>(null); // a right-click on a list vessel renames it in the checklist
  const renameRow = (rowId: number | undefined) => (name: string) => {
    if (detail.data && rowId != null) rename.mutate({ sap: detail.data.sap, boat: detail.data.rowId, rowId, name });
  };
  // stacked: open the checklist at the top of the page, and on the way back land on the vessel just left
  const pick = (row: number) => { setBoat(row); if (stacked) scrollContent()?.scrollTo({ top: 0 }); };
  const back = () => {
    const prev = boat;
    setBoat(null);
    if (prev == null) return;
    requestAnimationFrame(() => document.querySelector(`.asm-list [data-row="${prev}"]`)?.scrollIntoView({ block: 'center' }));
  };
  const toggleSel = (row: number) => setSelected((prev) => { const n = new Set(prev); n.has(row) ? n.delete(row) : n.add(row); return n; });

  // switching season re-keys the vessel list; the previously selected SAP (and the print set) belong
  // to the old season, so clear them — otherwise the detail panel keeps requesting a SAP that isn't in
  // the new season and shows a stuck "Vessel not found" error.
  useEffect(() => { setBoat(null); setSelected(new Set()); }, [activeSeason]);

  // the models on the sheets with how many boats each has, for the model filter ("GT7 (5)").
  const models = useMemo(() => {
    const n = new Map<string, number>();
    for (const v of list.data?.vessels ?? []) if (v.name && v.model) n.set(v.model, (n.get(v.model) ?? 0) + 1);
    return [...n].sort((a, b) => a[0].localeCompare(b[0]));
  }, [list.data]);

  const vessels = useMemo(() => {
    const all = (list.data?.vessels ?? []).filter((v) => v.name);
    const query = fold(q.trim());
    return all.filter((v) => {
      if (hideNoSap && !hasSap(v.sap)) return false;
      if (model !== 'all' && v.model !== model) return false;
      return hasText(query, v.name, v.sap, v.model);
    });
  }, [list.data, q, model, hideNoSap]);
  // boats still waiting for a SAP (so the toggle can show a count).
  const noSapCount = useMemo(
    () => (list.data?.vessels ?? []).filter((v) => v.name && !hasSap(v.sap)).length,
    [list.data],
  );
  // this season's boats that aren't on an assembly sheet yet (from the Overview), for the chosen model
  const notOnSheet = useMemo(() => {
    const query = fold(q.trim());
    return (list.data?.notOnSheet ?? []).filter((v) => (model === 'all' || v.model === model) && hasText(query, v.name, v.sap, v.model));
  }, [list.data, model, q]);

  const allShownSelected = vessels.length > 0 && vessels.every((v) => selected.has(v.rowId));
  const toggleAllShown = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (allShownSelected) vessels.forEach((v) => n.delete(v.rowId));
      else vessels.forEach((v) => n.add(v.rowId));
      return n;
    });

  // only the currently-shown selected vessels get printed, so the count reflects exactly that (a SAP
  // hidden by the search/model filter, or left over from another season, isn't in this list).
  const printSaps = vessels.filter((v) => selected.has(v.rowId));

  // fetch each selected vessel's checklist and print them (one per page).
  const printSelected = async () => {
    if (!printSaps.length) return;
    // open the popup NOW, inside the click — window.open() after the await below would be blocked by
    // the browser and the print would silently do nothing.
    const w = openPrintWindow();
    if (!w) { setPrintErr(new Error('The print window was blocked. Allow pop-ups for this site and try again.')); return; }
    setPrintErr(null); setPrinting(true);
    try {
      const results = await Promise.allSettled(
        printSaps.map((v) => api.get<VesselDetail>(`/api/vessel?row=${v.rowId}&sap=${encodeURIComponent(v.sap)}${activeSeason ? `&season=${encodeURIComponent(activeSeason)}` : ''}`)),
      );
      const details = results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
      const failed = results.length - details.length;
      if (!details.length) { w.close(); setPrintErr(new Error('None of the selected vessels could be loaded - nothing to print.')); return; }
      const body = details.map((d, i) => {
        const rows = d.sections.map((sec) =>
          `<tr><td colspan="3" style="background:#eef2f4"><b>${esc(sec.name)}</b></td></tr>` +
          sec.tasks.map((t) => `<tr><td${t.d ? ' style="text-decoration:line-through;color:#999"' : ''}>${esc(t.n)}</td><td style="text-align:right">${t.w}h</td><td style="text-align:center;font-size:12px">${t.d ? '✓' : '☐'}</td></tr>`).join(''),
        ).join('');
        return `<section style="page-break-inside:avoid;${i > 0 ? 'page-break-before:always;' : ''}margin-bottom:14px">
          <h1>${esc(d.name)}</h1>
          <div class="sub">${esc(d.model)} · ${d.sap ? `SAP ${esc(d.sap)}` : 'no SAP yet'} · ${d.pct}% complete · ${d.remaining}h remaining</div>
          <table><thead><tr><th>Task</th><th style="width:60px">Hrs</th><th style="width:50px">Done</th></tr></thead><tbody>${rows}</tbody></table>
        </section>`;
      }).join('') + (failed ? `<p style="color:#a33;font-size:11px">${failed} selected vessel(s) could not be loaded and were skipped.</p>` : '');
      renderPrint(w, 'Assembly Progress', body);
    } catch (e) { w.close(); setPrintErr(e instanceof Error ? e : new Error('Printing failed.')); } finally { setPrinting(false); }
  };

  return (
    <>
      <PageHeader title="Assembly Progress">
        <input className="field" placeholder={isPhone ? 'Search vessels' : 'Search vessel / SAP / model'} aria-label="Search vessel / SAP / model" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field" value={model} onChange={(e) => setModel(e.target.value)} title="Filter by model">
          <option value="all">All models</option>
          {models.map(([m, n]) => <option key={m} value={m}>{m} ({n})</option>)}
        </select>
        {noSapCount > 0 && (
          <label className="muted check-row" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }} title="Hide boats that haven't been given a SAP yet">
            <input type="checkbox" checked={hideNoSap} onChange={(e) => setHideNoSap(e.target.checked)} />
            Hide boats without a SAP ({noSapCount})
          </label>
        )}
        <span className="muted page-count">{vessels.length} vessel{vessels.length === 1 ? '' : 's'}</span>
        <button className="btn btn-sm" disabled={!printSaps.length || printing} onClick={() => void printSelected()} title="Print the checklist of every ticked vessel shown">
          {printing ? 'Preparing…' : <><IconPrinter size={14} /> Print{printSaps.length ? ` (${printSaps.length})` : ''}</>}
        </button>
        <button className="btn btn-sm" disabled={list.isFetching} onClick={() => void list.refetch()}><SyncLabel busy={list.isFetching} /></button>
      </PageHeader>
      {printErr && <InlineError error={printErr} onDismiss={() => setPrintErr(null)} />}
      {/* the server answered from its last copy while it reads the sheets again; the page asks again by itself */}
      {(list.data?.stale || detail.data?.stale) && (
        <p className="asm-stale" role="status">
          Showing the copy read {df.ago(list.data?.asOf ?? detail.data?.asOf ?? Date.now())}; updating…
        </p>
      )}

      {list.isLoading && <LoadingState label="Loading production vessels…" />}
      {list.error && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

      {list.data && (
        <div className={`asm-progress${boat != null ? ' has-sel' : ''}`}>
          <div className="page-panel asm-list">
            <label className="muted check-row" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px 8px', fontSize: 11 }}>
              <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} title="Select all shown for print" />
              Select all for print{printSaps.length ? ` · ${printSaps.length} selected` : ''}
            </label>
            {!vessels.length && (
              <div className="muted" style={{ padding: '8px 4px' }}>
                {(list.data.vessels ?? []).filter((v) => v.name).length === 0
                  ? 'No production vessels for this season yet.'
                  : 'No vessels match your search / model filter.'}
              </div>
            )}
            {vessels.map((v, i) => (
              // many 26-27 rows are unfilled slots with a blank SAP, so `key={v.sap}` collapsed them all
              // to key="" — React then couldn't reconcile the list on a filter change and left stale rows
              // painted. Key on a stable, unique composite (index guarantees uniqueness) instead.
              <div key={`${v.sheetId}:${v.sap}:${v.name}:${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                {/* touch: the print tick gets its own 44px cell, clear of the vessel button */}
                <label className="asm-pick">
                  <input
                    type="checkbox"
                    checked={selected.has(v.rowId)}
                    onChange={() => toggleSel(v.rowId)}
                    title="Select for print"
                    aria-label={`Select ${v.name} for print`}
                  />
                </label>
                <button
                  className={`nav-item asm-vessel ${boat === v.rowId ? 'active' : ''}`}
                  style={{ flex: 1, padding: '8px 10px' }}
                  aria-pressed={boat === v.rowId}
                  data-row={v.rowId}
                  onClick={() => pick(v.rowId)}
                  onContextMenu={canWrite ? (e) => { e.preventDefault(); pick(v.rowId); setRenameBoat(v.rowId); } : undefined}
                  title={canWrite ? 'Right-click to rename' : undefined}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{v.name}</span>
                    <span>{v.pct}%</span>
                  </div>
                  <div className="asm-vmeta">{v.model || 'No model'} · {v.sap || <i>no SAP yet</i>} · {v.taskNdone}/{v.taskN}</div>
                  <ProgressBar pct={v.pct} done={v.pct >= 100} />
                </button>
              </div>
            ))}
            {notOnSheet.length > 0 && (
              <details className="asm-missing">
                <summary>
                  {notOnSheet.length} more {model === 'all' ? '' : `${model} `}boat{notOnSheet.length === 1 ? '' : 's'} in this season's Overview, not on an assembly sheet yet
                </summary>
                <ul>
                  {notOnSheet.map((v) => (
                    <li key={v.sap}><b>{v.name || '(no name)'}</b> <span className="muted">· {v.model || 'No model'} · SAP {v.sap}</span></li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          <div className="page-panel asm-detail">
            {boat != null && (
              // stacked only: back to the list, with the vessel and its % pinned while the checklist scrolls
              <div className="asm-detail-hd">
                <button type="button" className="btn btn-sm btn-ghost asm-back" onClick={back}>
                  <IconChevronRight size={16} /> All vessels
                </button>
                {detail.data && <><span className="asm-detail-nm">{detail.data.name}</span><span className="asm-detail-pct">{detail.data.pct}%</span></>}
              </div>
            )}
            {boat == null && (vessels.length
              ? <EmptyState title="Select a vessel" hint="Pick a vessel to see its task checklist." />
              : <EmptyState title="No vessels" hint="No production vessels to show for this season / filter." />)}
            {boat != null && detail.isLoading && <LoadingState />}
            {boat != null && detail.error && <ErrorState error={detail.error} />}
            {boat != null && detail.data && (
              <>
                <h2 className="page-title asm-detail-title" style={{ fontSize: 15 }}>
                  <Renamable
                    value={detail.data.name}
                    label="vessel"
                    canRename={canWrite && detail.data.rowId != null}
                    onRename={renameRow(detail.data.rowId)}
                    startEditing={renameBoat != null && renameBoat === detail.data.rowId}
                    onEditEnd={() => setRenameBoat(null)}
                  />{`\u00a0· ${detail.data.pct}%`}
                </h2>
                <div className="muted" style={{ marginBottom: 12 }}>
                  {detail.data.model || 'No model'} · {detail.data.sap || 'no SAP yet'} · {detail.data.remaining}h remaining
                </div>
                {toggle.isError && <InlineError error={toggle.error} onDismiss={() => toggle.reset()} />}
                {rename.isError && <InlineError error={rename.error} onDismiss={() => rename.reset()} />}
                {detail.data.sections.map((s) => (
                  <div key={s.name} style={{ marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>
                      <Renamable value={s.name} label="section" canRename={canWrite && s.r != null} onRename={renameRow(s.r)} />
                    </div>
                    {s.tasks.map((t) => (
                      <label key={t.r} className="asm-task">
                        <input
                          type="checkbox"
                          checked={t.d}
                          disabled={!canWrite}
                          onChange={(e) => toggle.mutate({ sap: detail.data!.sap, boat: detail.data!.rowId, rowId: t.r, done: e.target.checked })}
                        />
                        <Renamable
                          value={t.n}
                          label="task"
                          canRename={canWrite}
                          onRename={renameRow(t.r)}
                          style={{ textDecoration: t.d ? 'line-through' : 'none', opacity: t.d ? 0.6 : 1 }}
                        />
                        <span className="muted" style={{ marginLeft: 'auto' }}>{t.w}h</span>
                      </label>
                    ))}
                  </div>
                ))}
                {!canWrite && <div className="muted">You have read-only access to production tasks.</div>}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
